import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { usePageConfig } from '@hooks/usePageConfig'
import { useTableHighlight } from '@hooks/useTableHighlight'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import Summary from '@components/Summary/Summary'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import HeroBanner from '@components/HeroBanner/HeroBanner'
import FilterSelector from '@components/FilterSelector/FilterSelector'
import SortSelector from '@components/SortSelector/SortSelector'
import Tabs from '@components/Tabs/Tabs'
import ItemListSkeleton from '@components/skeletons/ItemListSkeleton/ItemListSkeleton'

import './Favorites.css'

export default function Favorites() {
  const intl = useIntl()

  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('queries')
  const gridRef = useRef<HTMLDivElement>(null)

  useTableHighlight(gridRef, searchQuery)

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.favorites' }),
        path: '/favorites'
      }
    ]
    return <ExternalHeaderContent hasBack crumbs={crumbs} />
  }, [intl])

  usePageConfig('favorites', headerContent)

  const tabs = useMemo(
    () => [
      { id: 'queries', label: intl.formatMessage({ id: 'tab.queries' }) },
      { id: 'dashboards', label: intl.formatMessage({ id: 'tab.dashboards' }) }
    ],
    [intl]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  return (
    <section id="favorites-content" className="main-content">
      <HeroBanner nameId="favorites">
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<Loader />}>
            <Summary
              items={[
                { label: 'queries.title', value: 234 },
                { label: 'dashboards', value: 124000 }
              ]}
              id="favorites"
            />
          </Suspense>
        </ErrorBoundary>
      </HeroBanner>
      <div id="favorites-display">
        <div className="container">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        <header>
          <div className="container">
            <search className="search-highlight">
              <input
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={intl.formatMessage({
                  id: 'search-data.placeholder'
                })}
              />
            </search>
            <div className="selectors-wrapper">
              <FilterSelector />
              <SortSelector />
            </div>
          </div>
        </header>
        <div id="favorites-grid" ref={gridRef}>
          <div className="container">
            <ErrorBoundary errorComponent={ErrorMessage}>
              <Suspense fallback={<ItemListSkeleton />}>
                <ItemListSkeleton />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </section>
  )
}
